// Renders the active workflow step + next-step preview. Owns the per-step
// motion flash that accompanies forward/back transitions.
//
// `compactText` lives in debug.js but we reimplement the trivial helpers here
// to avoid a backwards dependency.

import { DEFAULT_WORKFLOW_ID } from "./constants.js";
import { els } from "./dom.js";
import { getLanguage, t } from "./i18n.js";
import { scheduleCurrentStepFit } from "./text-fit.js";

const workflowImageCache = new Map();

const NESTED_TEMPLATE_KEYS = [
  "main_component_steps",
  "per_additional_item_steps",
  "epilogue_steps",
];

function collectImageUrl(value, into) {
  const url = String(value || "").trim();
  if (url) into.add(url);
}

function workflowImageUrls(workflow) {
  const steps = Array.isArray(workflow?.steps) ? workflow.steps : [];
  const urls = new Set();
  for (const step of steps) {
    collectImageUrl(step?.image, urls);
    const scan = step?.document_scan;
    if (!scan || typeof scan !== "object") continue;
    for (const key of NESTED_TEMPLATE_KEYS) {
      const templates = scan[key];
      if (!Array.isArray(templates)) continue;
      for (const tmpl of templates) {
        collectImageUrl(tmpl?.image, urls);
      }
    }
  }
  return [...urls];
}

export function preloadStepImage(url) {
  const key = String(url || "").trim();
  if (!key) return Promise.resolve(null);

  const cached = workflowImageCache.get(key);
  if (cached) return cached.promise;

  const img = new Image();
  img.decoding = "async";
  img.loading = "eager";

  const entry = { url: key, img, loaded: false, failed: false, promise: null };
  entry.promise = new Promise((resolve) => {
    img.onload = () => {
      entry.loaded = true;
      resolve(entry);
    };
    img.onerror = () => {
      entry.failed = true;
      resolve(entry);
    };
  });

  workflowImageCache.set(key, entry);
  img.src = key;
  return entry.promise;
}

export async function preloadWorkflowImagesInOrder(workflow) {
  const urls = workflowImageUrls(workflow);
  for (const url of urls) {
    await preloadStepImage(url);
  }
  return urls.length;
}

export function renderCurrentStepImage(imageUrl) {
  const url = String(imageUrl || "").trim();
  const shell = els.currentStepImageShell;
  const img = els.currentStepImage;
  const card = els.currentStepCard;

  if (card) card.dataset.hasImage = url ? "true" : "false";
  if (!shell || !img) {
    scheduleCurrentStepFit();
    return;
  }

  if (!url) {
    shell.hidden = true;
    shell.dataset.state = "empty";
    img.removeAttribute("src");
    img.removeAttribute("data-image-url");
    scheduleCurrentStepFit();
    return;
  }

  shell.hidden = false;
  shell.dataset.state = "loading";
  img.dataset.imageUrl = url;
  img.removeAttribute("src");

  void preloadStepImage(url).then((entry) => {
    if (!entry || img.dataset.imageUrl !== url) return;

    if (entry.failed) {
      shell.hidden = true;
      shell.dataset.state = "error";
      if (card) card.dataset.hasImage = "false";
      scheduleCurrentStepFit();
      return;
    }

    if (img.getAttribute("src") !== url) img.src = url;
    shell.hidden = false;
    shell.dataset.state = "loaded";
    if (card) card.dataset.hasImage = "true";
    scheduleCurrentStepFit();
  });
}

let stepCardMotionTimer = null;

export function flashStepCardMotion(motion, durationMs = 650) {
  const card = els.currentStepCard;
  if (!card) return;

  if (stepCardMotionTimer) {
    clearTimeout(stepCardMotionTimer);
    stepCardMotionTimer = null;
  }
  card.dataset.motion = motion;
  stepCardMotionTimer = setTimeout(() => {
    if (card.dataset.motion === motion) delete card.dataset.motion;
    stepCardMotionTimer = null;
  }, durationMs);
}

export function clearStepCardMotion() {
  if (stepCardMotionTimer) {
    clearTimeout(stepCardMotionTimer);
    stepCardMotionTimer = null;
  }
  if (els.currentStepCard) delete els.currentStepCard.dataset.motion;
}

export function renderWorkflowState(step) {
  if (!step) return;

  const previousIndex = Number(els.currentStepCard?.dataset.stepIndex || 0);
  const currentIndex = Number(step.step_index || 0);
  const stepWord =
    getLanguage() === "de" ? "Schritt" : "Step";
  const ofWord = getLanguage() === "de" ? "von" : "of";

  if (els.workflowTitle) {
    els.workflowTitle.textContent = step.title || t("step.workflow_title_placeholder");
  }
  if (els.currentStepCounter) {
    els.currentStepCounter.textContent = `${stepWord} ${step.step_index} ${ofWord} ${step.step_total}`;
  }
  if (els.currentStepTitle) {
    els.currentStepTitle.textContent =
      step.step_name || t("step.fallback_index", { idx: step.step_index });
  }
  if (els.currentStepInstruction) {
    els.currentStepInstruction.textContent = step.instruction || "";
  }

  renderCurrentStepImage(step.image);
  scheduleCurrentStepFit();

  const nextStep = step.next_step || null;

  if (nextStep) {
    if (els.nextStepPreview) els.nextStepPreview.dataset.empty = "false";
    if (els.nextStepEyebrow) {
      els.nextStepEyebrow.textContent = t("step.next_with_indices", {
        idx: nextStep.step_index,
        total: nextStep.step_total,
      });
    }
    if (els.nextStepTitle) {
      els.nextStepTitle.textContent =
        nextStep.step_name || t("step.fallback_index", { idx: nextStep.step_index });
    }
    if (els.nextStepInstruction) {
      els.nextStepInstruction.textContent = nextStep.instruction || "";
    }
  } else {
    if (els.nextStepPreview) els.nextStepPreview.dataset.empty = "true";
    if (els.nextStepEyebrow) els.nextStepEyebrow.textContent = t("step.next_eyebrow");
    if (els.nextStepTitle) els.nextStepTitle.textContent = t("step.finish_label");
    if (els.nextStepInstruction) {
      els.nextStepInstruction.textContent = t("step.finish_description");
    }
  }

  if (els.currentStepCard && currentIndex) {
    els.currentStepCard.dataset.stepIndex = String(currentIndex);
    if (previousIndex && currentIndex !== previousIndex) {
      const direction = currentIndex > previousIndex ? "next" : "prev";
      flashStepCardMotion(`move-${direction}`, 720);
    }
  }
}

export function selectedWorkflowId() {
  return els.workflowSel?.value || DEFAULT_WORKFLOW_ID;
}

function compact(text, maxLen) {
  const s = String(text || "").replace(/\s+/g, " ").trim();
  if (s.length <= maxLen) return s;
  return `${s.slice(0, maxLen)}…`;
}

// Build the per-action prompt the browser sends so the model knows precisely
// what to do for BEGIN/CHECK/NEXT/PREV. Server uses the same step state, so
// these messages stay short.
//
// Tool/function names (``next_step``, ``prev_step``, ``interrupt``,
// ``set_step``) and the action markers (BEGIN/CHECK/NEXT/PREV) MUST stay
// English in every language — the model recognises those literals and the
// SDK binds tool calls by exact name. Only the explanatory text varies.
export function buildModelStepInstruction(action, step) {
  const normalized = String(action || "check").toLowerCase();
  if (!step) return normalized.toUpperCase();

  const idx = Number(step.step_index || 1);
  const total = Number(step.step_total || 0);
  const isDe = getLanguage() === "de";
  const fallbackName = isDe ? `Schritt ${idx}` : `Step ${idx}`;
  const name = step.step_name || step.step_id || fallbackName;
  const check = compact(step.completion_check || step.instruction || "", 220);
  const doNow = compact(step.instruction || "", 160);

  if (isDe) {
    if (normalized === "begin" || normalized === "start") {
      return (
        `BEGIN. App-Schritt=${idx}/${total} ${name}. ` +
        `Aktuelle Anweisung=${doNow}. ` +
        `Folge dem BEGIN-Verhalten. Verwende den App-Schritt als Quelle der Wahrheit. ` +
        `Antworte auf Deutsch.`
      );
    }
    if (normalized === "check") {
      const finalRule =
        idx >= total && total > 0
          ? " Wenn der letzte Schritt klar abgeschlossen ist, rufe next_step genau einmal auf und sage danach nur: Fertig."
          : "";
      return (
        `CHECK. App-Schritt=${idx}/${total} ${name}. ` +
        `Fertig wenn=${check}. ` +
        `Wenn der Benutzer offensichtlich eine spätere oder unumkehrbare Aktion ausführt, während dieser oder ein erforderlicher früherer Schritt fehlt, rufe zuerst interrupt mit dem passenden step_index auf. ` +
        `Andernfalls prüfe nur das Kriterium des aktiven Schritts. ` +
        `Wenn klar abgeschlossen, rufe next_step genau einmal auf. ` +
        `Wenn nicht klar abgeschlossen, rufe kein Schritt-Tool auf und sage nur: Es fehlt noch: [was fehlt oder unvollständig ist]. ` +
        `Überspringe nicht mehrere Schritte. Rufe set_step nicht auf. Fordere keine Screenshots an. ` +
        `Antworte auf Deutsch.` +
        finalRule
      );
    }
    if (normalized === "next") {
      return (
        `NEXT. App-Schritt=${idx}/${total} ${name}. ` +
        `Manuelle Übersteuerung. Wenn nicht am letzten Schritt, rufe next_step genau einmal auf. ` +
        `Sage nach der Tool-Antwort nur die zurückgegebene aktuelle Schrittanweisung. ` +
        `Wenn bereits am letzten Schritt, rufe next_step nur auf, wenn der letzte Schritt klar abgeschlossen ist; sonst sage nur: Es fehlt noch: [was fehlt oder unvollständig ist]. ` +
        `Rufe set_step nicht auf. Überspringe nicht mehrere Schritte. Antworte auf Deutsch.`
      );
    }
    if (normalized === "prev") {
      return (
        `PREV. App-Schritt=${idx}/${total} ${name}. ` +
        `Manuelle Übersteuerung. Wenn du nicht bereits in Schritt 1 bist, rufe prev_step genau einmal auf. ` +
        `Sage nach der Tool-Antwort nur die zurückgegebene aktuelle Schrittanweisung. Antworte auf Deutsch.`
      );
    }
    return (
      `${normalized.toUpperCase()}. App-Schritt=${idx}/${total} ${name}. ` +
      `Jetzt tun=${doNow}. Rufe set_step nicht auf. Fordere keine Screenshots an. Antworte auf Deutsch.`
    );
  }

  // English (default, unchanged)
  if (normalized === "begin" || normalized === "start") {
    return (
      `BEGIN. App step=${idx}/${total} ${name}. ` +
      `Current instruction=${doNow}. ` +
      `Follow BEGIN behavior. Use the app step as source of truth.`
    );
  }

  if (normalized === "check") {
    const finalRule =
      idx >= total && total > 0
        ? " If the final step is clearly complete, call next_step exactly once, then after the tool response say only: Finish."
        : "";
    return (
      `CHECK. App step=${idx}/${total} ${name}. ` +
      `Done when=${check}. ` +
      `First, if the user is clearly doing a later or irreversible action while this or an earlier required step is missing, call interrupt with the matching step_index. ` +
      `Otherwise inspect only the active step criterion. ` +
      `If clearly complete, call next_step exactly once. ` +
      `If not clearly complete, do not call a step tool and say only: Still missing: [what is absent or incomplete]. ` +
      `Do not skip multiple steps. Do not call set_step. Do not request screenshots.` +
      finalRule
    );
  }

  if (normalized === "next") {
    return (
      `NEXT. App step=${idx}/${total} ${name}. ` +
      `Manual override. If not on the final step, call next_step exactly once. ` +
      `After the tool response, say only the returned current step instruction. ` +
      `If already on the final step, call next_step only if the final step is clearly complete; otherwise say only: Still missing: [what is absent or incomplete]. ` +
      `Do not call set_step. Do not skip multiple steps.`
    );
  }

  if (normalized === "prev") {
    return (
      `PREV. App step=${idx}/${total} ${name}. ` +
      `Manual override. If not already on Step 1, call prev_step exactly once. ` +
      `After the tool response, say only the returned current step instruction.`
    );
  }

  return (
    `${normalized.toUpperCase()}. App step=${idx}/${total} ${name}. ` +
    `Do now=${doNow}. Do not call set_step. Do not request screenshots.`
  );
}
